import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useRouteMatch } from "react-router-dom";
import SyntaxHighlighter from "react-syntax-highlighter";
import { docco } from "react-syntax-highlighter/dist/esm/styles/hljs";
import { Button, Col, Row, Container } from "reactstrap";
import ApiCaller from "api/apiCaller";
import Loading from "components/Loading";
import style from "react-syntax-highlighter/dist/esm/styles/hljs/agate";
import { UserInfo } from "model/user";
import api from "api";
import { ApiResponse } from "model/response";
import {
  TransformationDetailResponse,
  Transformation,
} from "model/transformation";
import { Popup } from "components/Popup";
import TransformationGraph from '../../components/TransformationGraph';
import "./index.css";

interface DetailData {
  transformation: Transformation | null;
  loading: boolean;
}

interface ModalData {
  isOpen: boolean;
  title: string;
  type: string;
  description: string;
  onPressSubmit: Function;
  onPressCancel: Function;
}

export default function TransformationDetail(props: any) {
  const match = useRouteMatch("/transformation/:transformation_id");
  const params: any = match?.params;
  const transformationId = params?.transformation_id;

  const companyId: any = api.getCurrentCompanyId();
  const [currentUser, setCurrentUser] = useState<UserInfo | null>(null);
  const [detailData, setDetailData] = useState<DetailData>({
    transformation: null,
    loading: true,
  });
  const [modalData, setModalData] = useState<ModalData>({
    isOpen: false,
    title: "",
    type: "confirm",
    description: "",
    onPressCancel: () => {},
    onPressSubmit: () => {},
  });
  const [isExecuting, setIsExecuting] = useState<boolean>(false);

  useEffect(() => {
    initData();
  }, []);

  const initData = async () => {
    const user = api.getCurrentUser();
    setCurrentUser(user);
    const res: ApiResponse = await ApiCaller.get(
      `transformation/${transformationId}`
    );
    if (res?.status === "ok") {
      const data: TransformationDetailResponse = res?.data;
      setDetailData({
        ...detailData,
        ...{
          transformation: data?.transformation,
          loading: false,
        },
      });
    } else {
      setDetailData({
        ...detailData,
        ...{
          loading: false,
        },
      });
    }
  };

  const deleteScheduleQuery = async () => {
    setDetailData({
      ...detailData,
      ...{
        loading: true,
      },
    });
    const res: ApiResponse = await ApiCaller.delete(
      `transformation/${transformationId}`
    );
    if (res?.status === "ok") {
      setDetailData({
        ...detailData,
        ...{
          loading: false,
        },
      });
      props.history.goBack();
    } else {
      setDetailData({
        ...detailData,
        ...{
          loading: false,
        },
      });
    }
    setModalData({ ...modalData, isOpen: false });
  };

  const onCreateTableFromStagingClick = async () => {
    const partitionToRecreateFrom = prompt('Specify yyyy= partition you want to scan (comma seperated)', '');
    if (!partitionToRecreateFrom) return;
    const res: ApiResponse = await ApiCaller.post(
      `transformation/${transformationId}/create_table_from_staging?partition=${partitionToRecreateFrom}`
    );
    console.log(res);
  }

  const executeTransformation = async () => {
    setIsExecuting(true);
    const res: ApiResponse = await ApiCaller.post(
      `transformation/${transformationId}/execute`
    );
    if (res?.status === "ok") {
      setIsExecuting(false);
    } else {
      
    }
  }

  if (detailData?.loading) return <Loading />;

  return (
    <Container>
      <Popup
        type={modalData?.type}
        isOpen={modalData?.isOpen}
        title={modalData?.title}
        description={modalData?.description}
        onPressSubmit={modalData?.onPressSubmit}
        onPressCancel={modalData?.onPressCancel}
      />
      <Row>
        <Col xs={12}>
          <h3>
            {detailData?.transformation?.name}
          </h3>
          {detailData?.transformation?.code_name &&
            <p>Code name: {detailData?.transformation?.code_name}</p>
          }
        </Col>
        <Col xs={12}>
          <div style={styles.controls}>
            <Button
              className="sc-btn"
              size="sm"
              color="success"
              onClick={executeTransformation}
            >
              <div>
                <i className="material-icons">play_arrow</i>
                <span>Run</span>
              </div>
            </Button>{" "}
            <Button
              className="sc-btn"
              size="sm"
              color="primary"
              tag={Link}
              to={{ pathname: `/transformation/${transformationId}/run_log` }}
            >
              <div>
                <i className="material-icons">list</i>
                <span>Run log</span>
              </div>
            </Button>{" "}
            <Button
              className="sc-btn"
              size="sm"
              color="primary"
              tag={Link}
              to={{ pathname: `/transformation/${transformationId}/edit` }}
            >
              <div>
                <i className="material-icons">create</i>
                <span>Edit</span>
              </div>
            </Button>{" "}
            <Button
              className="sc-btn"
              size="sm"
              color="danger"
              onClick={() => {
                setModalData({
                  isOpen: true,
                  title: "Warning",
                  type: "confirm",
                  description: `Are you sure want to delete this item ?`,
                  onPressSubmit: deleteScheduleQuery,
                  onPressCancel: () => {
                    setModalData({ ...modalData, ...{ isOpen: false } });
                  },
                });
              }}
            >
              <div>
                <i className="material-icons">clear</i>
                <span>Delete</span>
              </div>
            </Button>{" "}
            <Button
              className="sc-btn"
              size="sm"
              color="secondary"
              onClick={onCreateTableFromStagingClick}
            >
              <div>
                <i className="material-icons">not_started</i>
                <span>Create table from staging</span>
              </div>
            </Button>{" "}
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <div style={styles.info}>
            <span>Query info</span>
            <ul>
              <li>
                <span style={styles.infoHeading}>Company</span>:{" "}
                {
                  currentUser?.companies.find(
                    (company) => company.id == companyId
                  )?.name
                }
              </li>
              <li>
                <span style={styles.infoHeading}>Datasource</span>:{" "}
                {detailData?.transformation?.datasource}
              </li>
              <li>
                <span style={styles.infoHeading}>Destination dataset</span>:{" "}
                {detailData?.transformation?.dataset_name}
              </li>
              <li>
                <span style={styles.infoHeading}>Destination table</span>:{" "}
                {detailData?.transformation?.table_name}
              </li>
              <li>
                <span style={styles.infoHeading}>Processing Location</span>:{" "}
                {detailData?.transformation?.region}
              </li>
              <li>
                <span style={styles.infoHeading}>User</span>:{" "}
                {detailData?.transformation?.full_name}
              </li>
              <li>
                <span style={styles.infoHeading}>Merge mode</span>:{" "}
                {detailData?.transformation?.merge_mode}
              </li>
              {/* <li>
                <span style={styles.infoHeading}>Write Disposition</span>:{" "}
                {detailData?.transformation?.write_disposition}
              </li> */}
              <li>
                <span style={styles.infoHeading}>Created at (UTC)</span>:{" "}
                {detailData?.transformation?.created_on}
              </li>
              <li>
                <span style={styles.infoHeading}>Updated at (UTC)</span>:{" "}
                {detailData?.transformation?.changed_on}
              </li>
            </ul>
          </div>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <TransformationGraph
            graph={detailData?.transformation?.query_graphs}
            readOnly
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <span>Query SQL</span>
          <div style={styles.queryContainer}>
            <SyntaxHighlighter
              language="sql"
              style={docco}
              // showInlineLineNumbers
            >
              {detailData?.transformation?.query || ""}
            </SyntaxHighlighter>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

const styles: { [name: string]: React.CSSProperties } = {
  controls: {
    marginTop: 12,
    marginBottom: 12,
  },
  info: {
    marginTop: 12,
    marginBottom: 12,
  },
  infoHeading: {
    fontWeight: 700,
  },
  queryContainer: {
    marginTop: 12,
  },
};
